/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.html.dom.create
import kotlinx.html.*
import kotlinx.html.js.*
import org.w3c.dom.*
import kotlinx.coroutines.*
import ApiService

class AccountLinkingComponent(
    private val apiService: ApiService,
    private val daysRequested: Int = 90,
    private val onConnect: (accessToken: String) -> Unit = {},
    private val onBack: () -> Unit = {},
    private val onImportStatements: () -> Unit = {},
    private val onSkip: () -> Unit = {}
) {
    private val plaidLinkHelper = PlaidLinkHelper(apiService)
    private var isConnecting = false
    private var isFetchingHistoricalData = false
    private var retryCount = 0
    private var hasSuccessfulConnection = false
    
    fun render(): HTMLElement {
        return document.create.div {
            style = """
                display: flex;
                flex-direction: column;
                min-height: 100vh;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                background: linear-gradient(180deg, #0a0a0a 0%, #141418 100%);
                padding: 20px;
            """
            
            // Navigation Header
            div {
                style = """
                    display: flex;
                    justify-content: flex-start;
                    margin-bottom: 40px;
                """
                
                button {
                    style = """
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        background: none;
                        border: none;
                        color: #00d9ff;
                        font-size: 16px;
                        cursor: pointer;
                        padding: 8px 12px;
                        border-radius: 6px;
                        transition: background-color 0.2s;
                    """
                    onClickFunction = {
                        onBack()
                    }

                    +"← ${i18n.StringKey.NAV_BACK.tr()}"
                }
            }
            
            // Main Content
            div {
                style = """
                    display: flex;
                    justify-content: center;
                    flex: 1;
                    align-items: center;
                """

                // Show progress bar when fetching historical data
                if (isFetchingHistoricalData) {
                    div {
                        style = """
                            background: rgba(20, 20, 24, 0.95);
                            border-radius: 16px;
                            box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                            padding: 48px;
                            width: 100%;
                            max-width: 480px;
                            text-align: center;
                            backdrop-filter: blur(10px);
                        """

                        div {
                            style = """
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                gap: 24px;
                            """

                            // Loading spinner
                            div {
                                style = """
                                    width: 64px;
                                    height: 64px;
                                    border: 4px solid #333;
                                    border-top: 4px solid #00d9ff;
                                    border-radius: 50%;
                                    animation: spin 1s linear infinite;
                                """
                            }

                            h1 {
                                style = """
                                    font-size: 24px;
                                    font-weight: bold;
                                    color: #ffffff;
                                    margin: 0;
                                """
                                +StringKey.ACCOUNT_LINKING_FETCHING_HISTORY.tr()
                            }

                            val daysLabel = when (daysRequested) {
                                730 -> "24 months"
                                365 -> "12 months"
                                180 -> "6 months"
                                else -> "3 months"
                            }

                            p {
                                style = """
                                    font-size: 16px;
                                    color: #999;
                                    line-height: 1.5;
                                    margin: 0;
                                """
                                +StringKey.ACCOUNT_LINKING_DOWNLOADING.tr().replace("%s", daysLabel)
                            }

                            // Progress bar
                            div {
                                style = """
                                    width: 100%;
                                    margin-top: 16px;
                                """

                                val progress = retryCount / 20.0
                                val percentage = (progress * 100).toInt()

                                p {
                                    style = """
                                        font-size: 14px;
                                        font-weight: 500;
                                        color: #999;
                                        margin: 0 0 8px 0;
                                    """
                                    +"$percentage% complete"
                                }

                                div {
                                    style = """
                                        width: 100%;
                                        height: 8px;
                                        background-color: #333;
                                        border-radius: 4px;
                                        overflow: hidden;
                                    """

                                    div {
                                        style = """
                                            width: ${percentage}%;
                                            height: 100%;
                                            background: linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%);
                                            transition: width 0.3s ease;
                                        """
                                    }
                                }
                            }
                        }
                    }
                } else {
                    // Original connection UI
                    div {
                        style = """
                            background: rgba(20, 20, 24, 0.95);
                            border-radius: 16px;
                            box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                            padding: 48px;
                            width: 100%;
                            max-width: 480px;
                            text-align: center;
                            backdrop-filter: blur(10px);
                        """

                    // Title and Description
                    div {
                        style = "margin-bottom: 32px;"

                        h1 {
                            style = """
                                font-size: 28px;
                                font-weight: bold;
                                color: #ffffff;
                                margin: 0 0 16px 0;
                                line-height: 1.2;
                            """
                            +StringKey.ACCOUNT_LINKING_TITLE.tr()
                        }

                        p {
                            style = """
                                font-size: 16px;
                                color: #999;
                                line-height: 1.5;
                                margin: 0;
                            """
                            +StringKey.ACCOUNT_LINKING_DESCRIPTION.tr()
                        }
                    }

                    // Security Features
                    div {
                        style = """
                            background: rgba(255, 255, 255, 0.03);
                            border: 1px solid rgba(255, 255, 255, 0.08);
                            border-radius: 12px;
                            padding: 24px;
                            margin-bottom: 32px;
                        """

                        div {
                            style = "display: flex; align-items: center; margin-bottom: 12px; text-align: left;"
                            span {
                                style = "font-size: 20px; margin-right: 12px;"
                                +"🔒"
                            }
                            span {
                                style = "font-size: 16px; color: #ccc;"
                                +StringKey.ACCOUNT_LINKING_SECURITY_ENCRYPTION.tr()
                            }
                        }

                        div {
                            style = "display: flex; align-items: center; margin-bottom: 12px; text-align: left;"
                            span {
                                style = "font-size: 20px; margin-right: 12px;"
                                +"🚫"
                            }
                            span {
                                style = "font-size: 16px; color: #ccc;"
                                +StringKey.ACCOUNT_LINKING_SECURITY_NO_CREDENTIALS.tr()
                            }
                        }

                        div {
                            style = "display: flex; align-items: center; margin-bottom: 12px; text-align: left;"
                            span {
                                style = "font-size: 20px; margin-right: 12px;"
                                +"✓"
                            }
                            span {
                                style = "font-size: 16px; color: #ccc;"
                                +StringKey.ACCOUNT_LINKING_SECURITY_READ_ONLY.tr()
                            }
                        }

                        div {
                            style = "display: flex; align-items: center; text-align: left;"
                            span {
                                style = "font-size: 20px; margin-right: 12px;"
                                +"🏦"
                            }
                            span {
                                style = "font-size: 16px; color: #ccc;"
                                +StringKey.ACCOUNT_LINKING_SECURITY_PLAID.tr()
                            }
                        }
                    }

                    // Connect Button
                    button {
                        id = "connect-btn"
                        style = """
                            width: 100%;
                            height: 56px;
                            background: ${if (isConnecting) "#555" else "linear-gradient(135deg, #00d9ff 0%, #004466 100%)"};
                            color: ${if (isConnecting) "#999" else "#ffffff"};
                            border: none;
                            border-radius: 12px;
                            font-size: 18px;
                            font-weight: 600;
                            cursor: ${if (isConnecting) "not-allowed" else "pointer"};
                            transition: all 0.2s ease;
                            margin-bottom: 24px;
                            box-shadow: ${if (isConnecting) "none" else "0 4px 16px rgba(0, 68, 102, 0.5)"};
                        """
                        onClickFunction = {
                            if (!isConnecting) {
                                handleConnect()
                            }
                        }

                        +if (isConnecting) StringKey.ACCOUNT_LINKING_CONNECTING.tr() else StringKey.ACCOUNT_LINKING_CONNECT.tr()
                    }

                    // Import from statements button (gradient border via nested divs)
                    div {
                        style = """
                            background: linear-gradient(135deg, #00d9ff 0%, #004466 100%);
                            border-radius: 12px;
                            padding: 2px;
                            width: 100%;
                            max-width: 400px;
                            margin-bottom: 12px;
                            cursor: pointer;
                        """
                        onClickFunction = { onImportStatements() }
                        div {
                            style = """
                                background: #1a1a1a;
                                border-radius: 10px;
                                padding: 14px 32px;
                                text-align: center;
                                color: #00d9ff;
                                font-size: 16px;
                                font-weight: 600;
                                transition: background 0.2s;
                            """
                            onMouseOverFunction = { (it.currentTarget as? HTMLElement)?.style?.background = "#222" }
                            onMouseOutFunction = { (it.currentTarget as? HTMLElement)?.style?.background = "#1a1a1a" }
                            +StringKey.IMPORT_FROM_STATEMENTS.tr()
                        }
                    }

                    // Skip for now
                    button {
                        style = """
                            background: none;
                            border: none;
                            color: #666;
                            font-size: 13px;
                            cursor: pointer;
                            padding: 8px;
                            margin-bottom: 16px;
                            opacity: 0.7;
                            transition: opacity 0.2s;
                        """
                        onMouseOverFunction = { (it.currentTarget as? HTMLElement)?.style?.opacity = "1" }
                        onMouseOutFunction = { (it.currentTarget as? HTMLElement)?.style?.opacity = "0.7" }
                        onClickFunction = {
                            onSkip()
                        }
                        +StringKey.ACCOUNT_LINKING_SKIP.tr()
                    }

                    // Privacy Notice
                    p {
                        style = """
                            font-size: 12px;
                            color: #666;
                            line-height: 1.4;
                            margin: 0;
                        """
                        +StringKey.ACCOUNT_LINKING_PRIVACY_NOTICE.tr()
                    }
                    }
                }
            }

            // Add CSS for spinner animation
            style {
                unsafe {
                    +"""
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    """
                }
            }
        }
    }
    
    private fun handleConnect() {
        if (isConnecting) return

        isConnecting = true

        GlobalScope.launch {
            try {
                // Auto-detect environment
                val environment = apiService.getPlaidEnvironment()
                console.log("🌍 Plaid environment: $environment")

                // Always use Plaid Link UI to allow choosing test users like user_ewa_user
                // (Previously sandbox mode auto-created users, bypassing Plaid Link)
                console.log("🔗 Using Plaid Link flow (environment: $environment)")
                usePlaidLinkFlow()

            } catch (e: Exception) {
                isConnecting = false
                console.error("Error detecting environment:", e)
                showConnectFailedDialog(e.message)
            }
        }
    }

    private fun usePlaidLinkFlow() {
        // Production flow: Use Plaid Link UI
        loadPlaidLinkScript {
            GlobalScope.launch {
                try {
                    plaidLinkHelper.initializePlaidLink(
                        daysRequested = daysRequested,
                        onSuccess = { accessToken ->
                            // Show progress UI immediately - don't go back to "Connect" screen
                            isFetchingHistoricalData = true
                            isConnecting = false
                            hasSuccessfulConnection = true
                            updateUI()  // Force re-render to show progress bar
                            // Start fetching transactions with retry
                            fetchTransactionsWithRetry()
                        },
                        onError = { error ->
                            isConnecting = false
                            console.error("Plaid Link error:", error)
                            showConnectFailedDialog(error)
                        }
                    )
                } catch (e: Exception) {
                    isConnecting = false
                    console.error("Error:", e)
                    showConnectFailedDialog(e.message)
                }
            }
        }
    }

    private fun fetchTransactionsWithRetry() {
        GlobalScope.launch {
            isFetchingHistoricalData = true
            retryCount = 0

            val maxRetries = 20
            val retryDelayMs = 15000L
            val expectedMinTransactions = maxOf(30, (daysRequested / 7) * 2)

            repeat(maxRetries) { attempt ->
                try {
                    console.log("Attempt ${attempt + 1}/$maxRetries: Fetching transactions...")
                    // Use forceRefresh on retries (after first attempt)
                    val useForceRefresh = attempt > 0
                    val response = apiService.getTransactionsFromServer(forceRefresh = useForceRefresh)

                    val transactionCount = response.transactions.size
                    console.log("📊 Received $transactionCount transactions (expected minimum: $expectedMinTransactions for $daysRequested days)")

                    // For 6 months (180 days), we expect at least 100 transactions
                    // Only retry if we have VERY few transactions (less than 5) - DFA typically gives ~8
                    val shouldRetry = when {
                        transactionCount < 5 && attempt < 3 -> true // Retry 3 times if basically no transactions
                        else -> false
                    }

                    if (shouldRetry) {
                        console.log("⚠️ Only received $transactionCount transactions for $daysRequested days request - likely incomplete. Retrying with force_refresh...")
                        retryCount = attempt + 1
                        // Re-render to update progress bar
                        updateUI()
                        delay(retryDelayMs)
                        return@repeat
                    }

                    console.log("✅ Received sufficient transactions ($transactionCount) - proceeding...")

                    // Success! Navigate to main app
                    // CRITICAL: Set flags BEFORE calling onConnect to prevent re-rendering during navigation
                    isFetchingHistoricalData = false
                    hasSuccessfulConnection = true

                    console.log("✅ Transactions ready! Navigating to main app...")
                    console.log("✅ Calling onConnect callback to navigate...")

                    // Pass the item_id (stored in localStorage by PlaidLinkHelper)
                    val itemId = window.localStorage.getItem("plaid_item_id") ?: ""
                    console.log("✅ Item ID: $itemId")

                    // Phase B: if there are unlinked accounts from a previous connection
                    // to the same institution, splice their history into the new accounts
                    // before navigating away. Failures are non-blocking.
                    runMergePromptIfNeeded(itemId)

                    // Navigate to transaction list - this should change the screen
                    onConnect(itemId)
                    console.log("✅ onConnect callback completed - navigation should have occurred")
                    return@launch

                } catch (e: Exception) {
                    when (e) {
                        is PlaidSyncingException -> {
                            // Plaid is still syncing, retry
                            console.log("⏳ Plaid still syncing, retry ${attempt + 1}/$maxRetries...")
                            retryCount = attempt + 1
                            updateUI()
                            delay(retryDelayMs)
                        }
                        else -> {
                            // Other error, stop retrying
                            isFetchingHistoricalData = false
                            showAppAlert("Failed to fetch transactions: ${e.message}")
                            return@launch
                        }
                    }
                }
            }

            // Max retries reached
            isFetchingHistoricalData = false
            showAppAlert("Transaction sync is taking longer than expected. Please try refreshing later.")
        }
    }

    /**
     * After Plaid Link succeeds and accounts are stored, ask the server whether
     * any unlinked accounts from a previous connection to the same institution
     * pair to the new ones. HIGH-confidence pairs (mask + type + subtype all
     * match) auto-merge silently. MEDIUM/LOW pairs prompt the user once.
     * Either branch is a no-op if no candidates exist (the common case after
     * a fresh first-time link, where there's no prior data to splice in).
     */
    private suspend fun runMergePromptIfNeeded(itemId: String) {
        if (itemId.isBlank()) return
        val candidates = try {
            apiService.getMergeCandidates(itemId).candidates
        } catch (e: Exception) {
            console.warn("Merge candidates fetch failed (non-fatal): ${e.message}")
            return
        }
        if (candidates.isEmpty()) return

        val (highPairs, lowerPairs) = candidates.partition { it.confidence == "HIGH" }
        var totalRestoredTxns = 0
        var totalAccountsMerged = 0
        var singleAccountLabel: String? = null

        suspend fun runMerge(toMerge: List<ApiService.MergeCandidate>) {
            if (toMerge.isEmpty()) return
            try {
                val resp = apiService.executeMerge(
                    toMerge.map { ApiService.MergeExecuteRequestPair(it.new_account_id, it.old_account_id) }
                )
                totalRestoredTxns += resp.results.sumOf { it.merged_transaction_count + it.reparented_transaction_count }
                totalAccountsMerged += resp.results.size
                singleAccountLabel = toMerge.firstOrNull()?.new_account_name
            } catch (e: Exception) {
                console.warn("Merge execute failed (non-fatal): ${e.message}")
            }
        }

        // 1. Auto-merge HIGH confidence pairs silently.
        runMerge(highPairs)

        // 2. Ask the user about anything below HIGH confidence. Single batch
        //    decision (yes/no) — granular per-pair UI lives in Settings later.
        if (lowerPairs.isNotEmpty()) {
            // Title and list are separate now: window.confirm could only take one
            // blob of text, so the question and the accounts it was about ran
            // together in the same paragraph.
            showAppConfirm(
                title = StringKey.MERGE_PENDING_CONFIRM_PROMPT.tr(lowerPairs.size.toString()),
                message = lowerPairs.joinToString("\n") { c ->
                    val mask = c.old_account_mask?.let { " (••$it)" } ?: ""
                    "• ${c.old_account_name}$mask — ${c.old_transaction_count} transactions"
                },
                confirmLabel = StringKey.ACTION_CONFIRM.tr(),
                // The confirm callback fires after this function has returned, so
                // the merge needs its own scope rather than the caller's.
            ) { GlobalScope.launch { runMerge(lowerPairs) } }
        }

        // 3. One-shot success toast (only when work happened).
        if (totalRestoredTxns > 0) {
            val toast = if (totalAccountsMerged == 1 && singleAccountLabel != null) {
                StringKey.MERGE_RESTORED_TOAST_ONE.tr(totalRestoredTxns.toString(), singleAccountLabel!!)
            } else {
                StringKey.MERGE_RESTORED_TOAST_MANY.tr(totalRestoredTxns.toString(), totalAccountsMerged.toString())
            }
            showAppAlert(toast)
        }
    }

    private fun updateUI() {
        // Re-render the component to update the progress bar
        val container = document.getElementById("account-linking-container")
        container?.innerHTML = ""
        container?.appendChild(render())
    }
    
    private fun setupEventHandlers() {
        // Add global functions for event handling
        window.asDynamic().handleConnect = {
            handleConnect()
        }
        
        window.asDynamic().handleBack = {
            onBack()
        }
    }
    
    fun attachToDOM(elementId: String) {
        val rootElement = document.getElementById(elementId)
        if (rootElement != null) {
            rootElement.appendChild(render())
            setupEventHandlers()
        }
    }
}
